// this is basically a doubly-linked list node, used to represent the acquisition queue
interface AcquisitionQueueNode {
  key: any;
  previous: null | AcquisitionQueueNode;
  next: null | AcquisitionQueueNode;
  resolve: (value?: boolean | PromiseLike<boolean>) => void;
}

/**
 * Promise-based semaphore for managing access to capacity-limited resources
 * eg. concurrency-limiting API requests
 *
 * Example code:
 *     const queryApiPool = new AsyncCountingSemaphore(10);
 *     if (await queryApiPool.acquire()) {
 *       await fetch(queryApiUrl);
 *       queryApiPool.release();
 *     }
 */
export class AsyncCountingSemaphore {
  private count: number;
  private maxCount: number;
  private acquisitionQueueHead: null | AcquisitionQueueNode;
  private acquisitionQueueTail: null | AcquisitionQueueNode;
  private acquisitions: Map<any, AcquisitionQueueNode>;

  constructor(maxCount: number) {
    if (!Number.isInteger(maxCount) || maxCount < 1) {
      throw new Error(`@maxCount argument must be a finite positive integer`);
    }

    this.count = 0;
    this.maxCount = maxCount;
    this.acquisitionQueueHead = null;
    this.acquisitionQueueTail = null;
    this.acquisitions = new Map<any, AcquisitionQueueNode>();
  }

  public acquire(key?: any): Promise<boolean> {
    const acquisitionKey = key === undefined ? Symbol() : key;
    if (this.acquisitions.has(acquisitionKey)) {
      // acquiring pending keys can cause deadlocks
      throw new Error(`Key "${acquisitionKey}" is already pending`);
    }
    return new Promise((resolve) => {
      this.pushToQueue(acquisitionKey, resolve);
      this.tryResolveNextAcquisition();
    });
  }

  public release() {
    if (this.count > 0) {
      this.count -= 1;
      this.tryResolveNextAcquisition();
    }
  }

  public abortPending() {
    while (this.acquisitions.size > 0 && this.acquisitionQueueHead) {
      const resolve = this.acquisitionQueueHead.resolve;
      this.removeFromQueue(this.acquisitionQueueHead.key);
      resolve(false);
    }
  }

  public abortPendingKey(key: any) {
    if (this.acquisitions.has(key)) {
      this.acquisitions.get(key)!.resolve(false);
      this.removeFromQueue(key);
    }
  }

  public getCount(): number {
    return this.count;
  }

  private pushToQueue(key: any, resolve: (value?: boolean | PromiseLike<boolean>) => void) {
    const node: AcquisitionQueueNode = {
      key,
      previous: null,
      next: null,
      resolve,
    };
    if (this.acquisitions.size === 0) {
      this.acquisitionQueueHead = node;
    }
    if (this.acquisitionQueueTail) {
      this.acquisitionQueueTail.next = node;
    }
    node.previous = this.acquisitionQueueTail;
    this.acquisitionQueueTail = node;
    this.acquisitions.set(key, node);
  }

  private removeFromQueue(key: any) {
    if (this.acquisitions.has(key)) {
      const node = this.acquisitions.get(key)!;
      if (node.previous) {
        node.previous.next = node.next;
      }
      if (node.next) {
        node.next.previous = node.previous;
      }
      if (this.acquisitionQueueHead === node) {
        this.acquisitionQueueHead = node.next;
      }
      if (this.acquisitionQueueTail === node) {
        this.acquisitionQueueTail = node.previous;
      }

      this.acquisitions.delete(key);
    }
  }

  private tryResolveNextAcquisition() {
    if (this.count < this.maxCount && this.acquisitions.size > 0 && this.acquisitionQueueHead) {
      this.count += 1;
      const resolve = this.acquisitionQueueHead.resolve;
      this.removeFromQueue(this.acquisitionQueueHead.key);
      resolve(true);
    }
  }
}
